/**
 * Reconciles a single index from a previous ordering to a new ordering,
 * using entity IDs as the stable identity.
 *
 * Returns `null` if the entity at `prevIndex` no longer exists in the
 * new ordering (either because the index was out of bounds or the
 * entity was deleted).
 *
 * @example
 * ```ts
 * // Entity 'b' moved from index 1 to index 0
 * reconcileSingleIndex(1, ['a', 'b', 'c'], new Map([['b', 0], ['a', 1], ['c', 2]]));
 * // → 0
 *
 * // Entity 'b' was deleted
 * reconcileSingleIndex(1, ['a', 'b', 'c'], new Map([['a', 0], ['c', 1]]));
 * // → null
 *
 * // Index out of bounds
 * reconcileSingleIndex(5, ['a', 'b'], new Map([['a', 0], ['b', 1]]));
 * // → null
 * ```
 */
export function reconcileSingleIndex(
  prevIndex: number,
  prevIds: string[],
  nextLookup: Map<string, number>
): number | null {
  const id = prevIds[prevIndex];

  if (id === undefined) {
    return null;
  }

  return nextLookup.get(id) ?? null;
}
